import React from "react";
import { ExecutionLogEntry } from "@/types/workflow";
import { cn } from "@/lib/utils";
import { Clock, Timer, Zap } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface ExecutionTimelineProps {
  entries: ExecutionLogEntry[] | null;
  startedAt: string;
  completedAt: string | null;
}

interface StepData {
  name: string;
  duration_ms: number;
  level: ExecutionLogEntry["level"];
}

// Extract steps with duration from execution log
const extractSteps = (entries: ExecutionLogEntry[]): StepData[] => {
  return entries
    .filter(entry => entry.duration_ms !== undefined && entry.duration_ms > 0)
    .map(entry => ({
      name: entry.step_name || entry.message,
      duration_ms: entry.duration_ms!,
      level: entry.level,
    }));
};

// Get color class based on duration
const getDurationColor = (durationMs: number): string => {
  if (durationMs < 1000) return "bg-green-500";
  if (durationMs < 5000) return "bg-amber-500";
  return "bg-red-500";
};

// Get text color class based on duration
const getDurationTextColor = (durationMs: number): string => {
  if (durationMs < 1000) return "text-green-600 dark:text-green-400";
  if (durationMs < 5000) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
};

// Format duration for display
const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

export const ExecutionTimeline: React.FC<ExecutionTimelineProps> = ({
  entries,
  startedAt,
  completedAt,
}) => {
  if (!entries || entries.length === 0) {
    return null;
  }

  const steps = extractSteps(entries);
  
  if (steps.length === 0) {
    return null;
  }

  const totalDuration = steps.reduce((acc, step) => acc + step.duration_ms, 0);
  const overallDuration = completedAt 
    ? new Date(completedAt).getTime() - new Date(startedAt).getTime()
    : totalDuration;

  return (
    <div className="space-y-3">
      <h4 className="font-medium flex items-center gap-2">
        <Timer className="h-4 w-4" />
        Stap Tijdlijn
      </h4>
      
      {/* Summary bar */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
        <Clock className="h-3 w-3" />
        <span>Totale duur: {formatDuration(overallDuration)}</span>
        <span className="text-muted-foreground/50">•</span>
        <span>{steps.length} stappen</span>
      </div>

      {/* Steps list */}
      <div className="space-y-2">
        {steps.map((step, index) => {
          const percentage = totalDuration > 0 
            ? Math.round((step.duration_ms / totalDuration) * 100) 
            : 0;
          
          return (
            <div 
              key={index} 
              className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg"
            >
              {/* Step indicator */}
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-muted text-xs font-medium">
                {index + 1}
              </div>
              
              {/* Step info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-medium truncate">
                    {step.name}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={cn("text-xs font-mono", getDurationTextColor(step.duration_ms))}>
                      {formatDuration(step.duration_ms)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({percentage}%)
                    </span>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={cn("h-full transition-all", getDurationColor(step.duration_ms))}
                    style={{ width: `${Math.max(percentage, 2)}%` }}
                  />
                </div>
              </div>

              {/* Slow indicator */}
              {step.duration_ms >= 5000 && (
                <Zap className="h-4 w-4 text-red-500 shrink-0" />
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span>&lt; 1s</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-amber-500" />
          <span>1-5s</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span>&gt; 5s</span>
        </div>
      </div>
    </div>
  );
};
